import { useFormikContext } from 'formik';
import type { ChampionshipFormValues } from 'modules/tournaments/ui/components/form-parts/rules';
import { GameRuleProps } from 'modules/tournaments/ui/components/form-parts/rules/types';
import React, { ReactNode, useEffect, useMemo } from 'react';
import { LabeledCheckbox, LabeledInput, SectionTitle } from '../fields';

interface ExtendedGameRuleProps extends GameRuleProps {
  children?: ReactNode;
}

const GameBase: React.FC<ExtendedGameRuleProps> = ({ sportType = 'general', children }) => {
  const context = useFormikContext<ChampionshipFormValues>();
  const { gamePeriodsNumber, periodTime } = useMemo(() => {
    const settings = context.values.settings;
    const gamePeriodsNumber = Number(settings.gamePeriodsNumber) || 0;
    const periodTime = Number(settings.periodTime) || 0;
    return { gamePeriodsNumber, periodTime };
  }, [context.values.settings]);

  useEffect(() => {
    // If either periods number or period time become non-positive, force countdownTimer to false
    if (gamePeriodsNumber <= 0 || periodTime <= 0) {
      if (context.values.settings.countdownTimer) {
        context.setFieldValue('settings.countdownTimer', false);
      }
    }
  }, [gamePeriodsNumber, periodTime, context.values.settings.countdownTimer, context.setFieldValue, context.values.settings]);

  return (
    <>
      <SectionTitle title="Игра" icon="⚙️" tooltip="Технические параметры матча." />

      <div className="row gx-4">
        <div className="col-6">
          <LabeledInput
            name="settings.gamePeriodsNumber"
            placeholder="0"
            label="Количество периодов в матче"
            alignRight={true}
            flexShrink={true}
            tooltip="Число таймов/периодов в игре (например, 2 в футболе, 3 в хоккее)."
            tooltipProps={{ position: 'input-right' }}
          />
          <LabeledInput
            name="settings.periodTime"
            placeholder="0 мин"
            label="Длительность основного периода"
            alignRight={true}
            flexShrink={true}
            tooltip="Длительность одного основного периода в минутах (без учёта остановок). Например, 45 для футбола."
            tooltipProps={{ position: 'input-right' }}
          />
          <LabeledInput
            name="settings.totalMatches"
            placeholder="Не указано"
            label="Количество матчей"
            containerClassName="d-flex align-items-center"
            alignRight={true}
            flexShrink={true}
            tooltip={
              <div style={{ whiteSpace: 'pre-line' }}>
                {'Количество игр в турнире.\n\nЭта настройка будет показывать прогресс завершённости турнира.'}
              </div>
            }
            tooltipProps={{ position: 'input-right' }}
          />
        </div>
        <div className="col-6">
          <LabeledInput
            name="settings.gameExtraPeriodsNumber"
            placeholder="0"
            label="Кол-во периодов дополнительного времени"
            alignRight={true}
            flexShrink={true}
            tooltip="Количество периодов дополнительного времени (например, 2 тайма в футболе)."
            tooltipProps={{ position: 'input-right' }}
          />

          <LabeledInput
            name="settings.periodExtraTime"
            placeholder="0 мин"
            label="Длительность дополнительного периода"
            alignRight={true}
            flexShrink={true}
            tooltip="Продолжительность одного дополнительного периода в минутах. (например, 15 минут в футболе)."
            tooltipProps={{ position: 'input-right' }}
          />

          <LabeledCheckbox
            label="Обратный отсчёт времени"
            name="settings.countdownTimer"
            disabled={gamePeriodsNumber <= 0 || periodTime <= 0}
            tooltip={
              <div style={{ whiteSpace: 'pre-line' }}>
                <span>
                  В режиме трансляции таймер будет идти в обратно направлении. Напрямую зависит от
                  <strong> длительности мачта</strong> и <strong>кол-во периодов в матче</strong>, они должны быть больше 0.
                </span>
                <br />
                <br />
                <span>
                  ⚠️ Время события матча будет записываться в зависимости от прошедшего времени, как если таймер отсчитывался с 00:00.
                </span>
              </div>
            }
            tooltipProps={{ position: 'input-right' }}
          />
        </div>
      </div>

      {/* Место для дополнительных полей */}
      {children}
    </>
  );
};

export default GameBase;
